import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { goalsApi } from "@/api/goals";
import { issuesApi } from "@/api/issues";
import type { Issue } from "@/api/issues";
import type { GoalMessage } from "@/api/goals";
import { queryKeys } from "@/lib/query-keys";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import {
  ArrowLeft, Plus, X, Send, RefreshCw, Loader2, Ban, PlayCircle,
  Bot, User, HelpCircle, MessageSquare, AlertOctagon, CheckCircle2,
} from "lucide-react";

const goalStatuses = ["active", "in_progress", "completed", "abandoned"] as const;
const issuePriorities = ["low", "medium", "high", "urgent"] as const;

const statusVariant: Record<string, "success" | "warning" | "default" | "danger"> = {
  active: "warning",
  in_progress: "default",
  completed: "success",
  abandoned: "secondary" as "default",
};

const issueStatusVariant: Record<string, "success" | "warning" | "default" | "danger"> = {
  backlog: "secondary" as "default",
  todo: "default",
  in_progress: "warning",
  in_review: "warning",
  blocked: "danger",
  done: "success",
  cancelled: "secondary" as "default",
};

const messageTypeIcons: Record<string, typeof MessageSquare> = {
  question: HelpCircle,
  update: MessageSquare,
  blocker: AlertOctagon,
  completion: CheckCircle2,
};

interface TaskForm {
  title: string;
  description: string;
  priority: string;
  assigneeAgentId: string;
}

const emptyTask: TaskForm = { title: "", description: "", priority: "", assigneeAgentId: "" };

export function GoalDetail() {
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: goal, isLoading: goalLoading } = useQuery({
    queryKey: queryKeys.goals.detail(goalId!),
    queryFn: () => goalsApi.get(goalId!),
    enabled: !!goalId,
  });

  const { data: progress } = useQuery({
    queryKey: queryKeys.goals.progress(goalId!),
    queryFn: () => goalsApi.progress(goalId!),
    enabled: !!goalId,
  });

  const { data: allIssues = [] } = useQuery({
    queryKey: queryKeys.issues.byCompany(tenantId),
    queryFn: () => issuesApi.list(tenantId),
  });

  const { data: messages = [] } = useQuery({
    queryKey: queryKeys.goals.messages(goalId!),
    queryFn: () => goalsApi.listMessages(goalId!),
    enabled: !!goalId,
    refetchInterval: 15000,
  });

  // Mark messages as read when viewing
  useEffect(() => {
    const hasUnread = messages.some((m: GoalMessage) => m.direction === "agent_to_human" && !m.readAt);
    if (hasUnread) {
      goalsApi.markMessagesRead(goalId!).then(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.goals.unreadCount(tenantId) });
      });
    }
  }, [messages, goalId, tenantId, queryClient]);

  const childIssues = allIssues.filter((i: Issue) => i.goalId === goalId);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => goalsApi.update(goalId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.detail(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.byCompany(tenantId) });
    },
  });

  // Plan form state
  const [showPlan, setShowPlan] = useState(false);
  const [tasks, setTasks] = useState<TaskForm[]>([{ ...emptyTask }]);

  const planMutation = useMutation({
    mutationFn: () =>
      goalsApi.plan(
        goalId!,
        tasks.filter((t) => t.title.trim()).map((t) => ({
          title: t.title,
          description: t.description || undefined,
          priority: t.priority || undefined,
          assigneeAgentId: t.assigneeAgentId || null,
        })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.detail(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.progress(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
      setShowPlan(false);
      setTasks([{ ...emptyTask }]);
    },
  });

  // Message reply
  const [replyText, setReplyText] = useState("");
  const replyMutation = useMutation({
    mutationFn: () => goalsApi.sendMessage(goalId!, replyText),
    onSuccess: () => {
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.messages(goalId!) });
    },
  });

  // Request agent review (may create tasks and update statuses)
  const reviewMutation = useMutation({
    mutationFn: () => goalsApi.triggerReview(goalId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.messages(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.detail(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.progress(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
    },
  });

  // Cancel a task and notify the agent
  const cancelTaskMutation = useMutation({
    mutationFn: async (issue: Issue) => {
      await issuesApi.update(issue.id, { status: "cancelled" });
      // Send a message to the agent so it knows not to work on this
      if (goal?.ownerId) {
        await goalsApi.sendMessage(
          goalId!,
          `Task cancelled: "${issue.title}" — Do not proceed with this task. It has been removed from the plan by the human.`,
          "update",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.progress(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.messages(goalId!) });
    },
  });

  // Unblock a task and notify the agent to resume
  const unblockTaskMutation = useMutation({
    mutationFn: async (issue: Issue) => {
      await issuesApi.update(issue.id, { status: "in_progress" });
      if (goal?.ownerId) {
        await goalsApi.sendMessage(
          goalId!,
          `Task unblocked: "${issue.title}" — This task is no longer blocked. Please resume work on it.`,
          "update",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.progress(goalId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.messages(goalId!) });
    },
  });

  const addTask = () => setTasks([...tasks, { ...emptyTask }]);
  const removeTask = (idx: number) => setTasks(tasks.filter((_, i) => i !== idx));
  const updateTask = (idx: number, field: keyof TaskForm, value: string) => {
    const updated = [...tasks];
    updated[idx] = { ...updated[idx], [field]: value };
    setTasks(updated);
  };

  if (goalLoading || !goal) {
    return <CenterPanel title="Goal"><p className="text-sm text-text-muted">Loading...</p></CenterPanel>;
  }

  const ownerAgent = agents.find((a) => a.id === goal.ownerId);
  const validTasks = tasks.filter((t) => t.title.trim());

  return (
    <CenterPanel>
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate(`/tenants/${tenantId}/goals`)}>
        <ArrowLeft size={14} className="mr-1" /> Back to Goals
      </Button>

      <div className="flex items-start justify-between mb-2">
        <h1 className="text-xl font-semibold text-text-primary">{goal.title}</h1>
        <Badge variant={statusVariant[goal.status] ?? "default"} className="capitalize ml-3">
          {goal.status.replace("_", " ")}
        </Badge>
      </div>

      {goal.description && (
        <div className="text-sm text-text-secondary mb-4 rounded-lg border border-border bg-surface-hover p-4 max-h-80 overflow-auto">
          <MarkdownRenderer content={goal.description} />
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-text-muted mb-6">
        {ownerAgent && <span>Agent: {ownerAgent.name}</span>}
        {goal.targetDate && <span>Target: {new Date(goal.targetDate).toLocaleDateString()}</span>}
        <span>Created: {new Date(goal.createdAt).toLocaleDateString()}</span>
      </div>

      {/* Status control */}
      <Card className="mb-4">
        <CardHeader><CardTitle className="text-sm">Status</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-1.5">
            {goalStatuses.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={goal.status === s ? "default" : "outline"}
                onClick={() => updateMutation.mutate({ status: s })}
                className="text-xs capitalize"
              >
                {s.replace("_", " ")}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Messages / conversation thread */}
      {(messages.length > 0 || goal.ownerId) && (
        <Card className="mb-4">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare size={14} />
                Messages
                {messages.filter((m: GoalMessage) => m.direction === "agent_to_human" && !m.readAt).length > 0 && (
                  <Badge variant="warning" className="text-[10px]">
                    {messages.filter((m: GoalMessage) => m.direction === "agent_to_human" && !m.readAt).length} new
                  </Badge>
                )}
              </CardTitle>
              {goal.ownerId && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={reviewMutation.isPending}
                  onClick={() => reviewMutation.mutate()}
                >
                  {reviewMutation.isPending ? (
                    <><Loader2 size={12} className="mr-1.5 animate-spin" />Reviewing...</>
                  ) : (
                    <><RefreshCw size={12} className="mr-1.5" />Ask Agent to Review</>
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-xs text-text-muted py-4 text-center">
                No messages yet. The agent will send messages here when it has questions or updates.
              </p>
            ) : (
              <div className="space-y-3 mb-4 max-h-96 overflow-auto">
                {messages.map((msg: GoalMessage) => {
                  const isAgent = msg.direction === "agent_to_human";
                  const MsgIcon = messageTypeIcons[msg.messageType] ?? MessageSquare;
                  const agentName = isAgent ? (agents.find((a) => a.id === msg.agentId)?.name || "Agent") : null;

                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-2.5 ${isAgent ? "" : "flex-row-reverse"}`}
                    >
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${isAgent ? "bg-accent/10" : "bg-surface-hover"}`}>
                        {isAgent ? <Bot size={14} className="text-accent" /> : <User size={14} className="text-text-muted" />}
                      </div>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isAgent ? "bg-surface-hover" : "bg-accent/10"}`}>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {isAgent && <MsgIcon size={11} className="text-text-muted" />}
                          <span className="text-[10px] text-text-muted font-medium">
                            {isAgent ? agentName : "You"} · {new Date(msg.createdAt).toLocaleString()}
                          </span>
                          {isAgent && (
                            <Badge variant={msg.messageType === "blocker" ? "danger" : msg.messageType === "question" ? "warning" : "default"} className="text-[8px] px-1 py-0">
                              {msg.messageType}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-text-primary"><MarkdownRenderer content={msg.content} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Reply input */}
            {goal.ownerId && (
              <div className="flex gap-2">
                <Input
                  placeholder="Reply to the agent..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && replyText.trim() && !replyMutation.isPending) {
                      e.preventDefault();
                      replyMutation.mutate();
                    }
                  }}
                />
                <Button
                  size="sm"
                  disabled={!replyText.trim() || replyMutation.isPending}
                  onClick={() => replyMutation.mutate()}
                >
                  <Send size={14} />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Progress section */}
      {progress && progress.total > 0 && (
        <Card className="mb-4">
          <CardHeader><CardTitle className="text-sm">Progress</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-2 flex-1 rounded-full bg-surface-hover overflow-hidden">
                <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${progress.percentComplete}%` }} />
              </div>
              <span className="text-sm font-medium text-text-primary">{progress.percentComplete}%</span>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-text-muted">
              {Object.entries(progress.byStatus).map(([status, count]) => (
                <span key={status} className="capitalize">{status.replace("_", " ")}: {count}</span>
              ))}
              <span>Total: {progress.total}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-text-primary">Tasks</h2>
          <Button size="sm" onClick={() => setShowPlan(!showPlan)}>
            <Plus size={16} className="mr-1.5" /> Plan Tasks
          </Button>
        </div>

        {showPlan && (
          <Card className="mb-4">
            <CardContent className="space-y-4 pt-4">
              {tasks.map((task, idx) => (
                <div key={idx} className="space-y-2 border-b border-border pb-3 last:border-b-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-muted font-medium">Task {idx + 1}</span>
                    {tasks.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeTask(idx)}><X size={12} /></Button>
                    )}
                  </div>
                  <Input placeholder="Task title *" value={task.title} onChange={(e) => updateTask(idx, "title", e.target.value)} />
                  <textarea
                    placeholder="Description (optional)"
                    value={task.description}
                    onChange={(e) => updateTask(idx, "description", e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm min-h-[60px] resize-y"
                  />
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <select value={task.priority} onChange={(e) => updateTask(idx, "priority", e.target.value)} className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
                      <option value="">Priority (default: medium)</option>
                      {issuePriorities.map((p) => (<option key={p} value={p} className="capitalize">{p}</option>))}
                    </select>
                    <select value={task.assigneeAgentId} onChange={(e) => updateTask(idx, "assigneeAgentId", e.target.value)} className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
                      <option value="">Unassigned</option>
                      {agents.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={addTask}><Plus size={14} className="mr-1" /> Add Task</Button>
                <Button size="sm" disabled={validTasks.length === 0 || planMutation.isPending} onClick={() => planMutation.mutate()}>
                  Create {validTasks.length} Task{validTasks.length !== 1 ? "s" : ""}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setShowPlan(false); setTasks([{ ...emptyTask }]); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {childIssues.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-sm text-text-muted">No tasks yet. Use "Plan Tasks" to add tasks to this goal.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {childIssues.map((issue: Issue) => {
              const assignee = agents.find((a) => a.id === issue.assigneeAgentId);
              const isTerminal = issue.status === "done" || issue.status === "cancelled";
              return (
                <Card
                  key={issue.id}
                  className={`transition-colors ${issue.status === "cancelled" ? "opacity-50" : "hover:border-accent/50 cursor-pointer"}`}
                  onClick={() => issue.status !== "cancelled" && navigate(`/tenants/${tenantId}/issues/${issue.id}`)}
                >
                  <CardContent className="flex items-center justify-between py-3">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${issue.status === "cancelled" ? "text-text-muted line-through" : "text-text-primary"}`}>
                        {issue.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-text-muted">
                        {assignee && <span>{assignee.name}</span>}
                        {issue.priority && <span className="capitalize">{issue.priority}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Badge variant={issueStatusVariant[issue.status] ?? "default"} className="capitalize">
                        {issue.status.replace("_", " ")}
                      </Badge>
                      {issue.status === "blocked" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          disabled={unblockTaskMutation.isPending}
                          onClick={() => unblockTaskMutation.mutate(issue)}
                        >
                          <PlayCircle size={12} className="mr-1" />
                          Unblock
                        </Button>
                      )}
                      {!isTerminal && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-text-muted hover:text-danger"
                          disabled={cancelTaskMutation.isPending}
                          onClick={() => {
                            if (confirm(`Cancel task "${issue.title}"? The agent will be notified.`)) {
                              cancelTaskMutation.mutate(issue);
                            }
                          }}
                        >
                          <Ban size={13} />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </CenterPanel>
  );
}
